import { Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List, Map } from 'immutable';
import { extend, isEqual } from 'lodash';
import { CustomValidators } from 'ngx-custom-validators';
import { IValueSwitch } from '../../../form-master/components/value-switch-field/value-switch-field.component';
import { IFormMasterBuilder } from '../../../form-master/form-master.interface';
import { TaxRate } from '../../../settings-data/sub-models/company-settings/tax-rate.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { CalculatorService } from '../../../shared/services/calculator/calculator.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';

@Injectable()
export class EditTaxBuilder implements IFormMasterBuilder {
  FORM_KEYS = {
    ID: 'id',
    INCLUSIVE: 'inclusive',
    RATES: 'rates',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _calculatorService: CalculatorService,
    private _translateService: TranslateService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  buildForm(allTaxes: List<Tax>, tax: Tax): FormGroup {
    const formKeys = this.FORM_KEYS;
    const canBeInclusive: boolean = this._calculatorService.checkTaxAccumulations(
      allTaxes,
      tax
    );
    const formArray = this.buildRatesFormArray(tax.rates);
    const formGroup = {
      [formKeys.INCLUSIVE]: canBeInclusive
        ? [tax.inclusive]
        : [{ value: false, disabled: true }],
      [formKeys.RATES]: formArray,
    };

    return this._formBuilder.group(formGroup);
  }

  buildRatesFormArray(taxRates: List<TaxRate>): FormArray {
    return this._formBuilder.array(
      taxRates
        .map((taxRate: TaxRate) => this.buildTaxRateControlInfo(taxRate.value))
        .toArray()
    );
  }

  buildTaxRateControlInfo(taxRate: any): FormControl {
    return this._formBuilder.control(taxRate, [
      Validators.required,
      CustomValidators.range([0, 999999999]),
    ]);
  }

  buildTaxTypes(
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): List<IValueSwitch> {
    return List([
      {
        key: this._translateService.instant('LABEL.INCLUSIVE'),
        value: true,
        trackingObject: trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'inclusive_tax',
          trackingScreenName
        ),
      },
      {
        key: this._translateService.instant('LABEL.EXCLUSIVE'),
        value: false,
        trackingObject: trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'exclusive_tax',
          trackingScreenName
        ),
      },
    ]);
  }

  buildEditTaxAdditionalProperties(
    currentTax: Tax,
    taxInfo: Map<string, any>
  ): any {
    const formKeys = this.FORM_KEYS;
    const currentRates = currentTax.rates.map(
      (taxRate: TaxRate) => taxRate.value
    );

    const taxPropertyTypeChanged =
      currentTax.inclusive !== taxInfo.get(formKeys.INCLUSIVE);
    const taxStandardRateChanged = !isEqual(
      currentRates.first(),
      taxInfo.get(formKeys.RATES).first()
    );
    const otherRatesChanged = !isEqual(
      currentRates.rest().toArray(),
      taxInfo.get(formKeys.RATES).rest().toArray()
    );

    return {
      tax_property_type_changed: taxPropertyTypeChanged,
      tax_standard_rate_changed: taxStandardRateChanged,
      other_rates_changed: otherRatesChanged,
    };
  }

  buildTax(currentTax: Tax, newTaxInfo: Map<string, any>): Tax {
    const formKeys = this.FORM_KEYS;

    const newTaxRates: List<TaxRate> = newTaxInfo.get(formKeys.RATES).map(
      (rateValue, key) =>
        new TaxRate({
          [TaxRate.KEYS.VALUE]: rateValue,
          [TaxRate.KEYS.IS_DEFAULT]: key === 0,
        })
    );
    const newTax = extend({}, currentTax.toJS(), {
      [Tax.KEYS.INCLUSIVE]: newTaxInfo.get(formKeys.INCLUSIVE, false),
      [Tax.KEYS.RATES]: newTaxRates,
    });

    return new Tax(newTax);
  }
}
